import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey } from '@mui/material/colors';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n, useStore, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { UpliftBetterHelpStackedIcon } from '@uplift/icons';
import { PrivateRoute, AddressVerificationProvider, AutoLogoutTrigger, ContextualNavigationHeaderProps, ContextualNavigationLayout, Loader } from '@uplift/ui-v2';
import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import { AcctStepper, TermsAndPrivacyFooter } from './components';
import { AboutYou, KeepGoing, PayorEnrollment, WelcomePage } from './pages';
import { OnboardingError } from './pages/onboarding-error';

export const OnboardingPage: React.VFC = () => {
  const { classes } = useStyles();
  const { push } = useHistory();
  const isSmall = useIsMobile('sm');
  const l10n = useL10n();
  const me = useCurrentuser();
  const { authStore } = useStore();
  const { path } = useRouteMatch();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);

  useEffect(() => {
    if (authStore.isLoggedIn && me?.status === 'regular') {
      push(ROUTING_CONFIG.main);
    }
  }, [ authStore.isLoggedIn, me?.status, push ]);

  const errorPage = path === ROUTING_CONFIG.onboardingError;

  const headerProps: ContextualNavigationHeaderProps = useMemo(() => {
    if (errorPage) {
      return null;
    }

    const title = (
      <Box justifyContent="center" textAlign="center">
        <Typography variant="h6" className={classes.title}>
          Set Up Your Account
        </Typography>
      </Box>
    );

    const props: ContextualNavigationHeaderProps = {
      leftButton: (
        <Box flex={1} className={classes.logo}>
          <UpliftBetterHelpStackedIcon />
        </Box>
      ),
      hideLogo: true
    };

    if (!isSmall) {
      props.centerBlock = title;
    }

    if (authStore.isLoggedIn) {
      props.rightButtonText = l10n.navigation.logout;
      props.onRightButtonClick = logoutTrigger;
    } else if (isSmall) {
      props.rightButton = title;
    }

    return props;
  }, [ authStore.isLoggedIn, classes.logo, classes.title, errorPage, isSmall, l10n.navigation.logout, logoutTrigger ]);

  return (
    <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
      <ContextualNavigationLayout
        headerProps={headerProps}
        fullWidth
        progress={!errorPage && <AcctStepper step={window.location.pathname} />}
      >
        <Box className={classes.root}>
          <Box className={classes.content}>
            <React.Suspense fallback={<Loader />}>
              <Switch>
                <Route exact path={ROUTING_CONFIG.onboardingError}>
                  <OnboardingError />
                </Route>
                <Route exact path={ROUTING_CONFIG.onboardingInvite}>
                  {!authStore.isLoggedIn && <WelcomePage authUrl={environment.authUrl} />}
                  {authStore.isLoggedIn && <KeepGoing />}
                </Route>
                <PrivateRoute
                  exact
                  path={ROUTING_CONFIG.onboardingGeneral}
                  redirectUrl={environment.authUrl}
                >
                  <AboutYou />
                </PrivateRoute>
                <PrivateRoute
                  exact
                  path={ROUTING_CONFIG.onboardingPayorEnrollment}
                  redirectUrl={environment.authUrl}
                >
                  <PayorEnrollment />
                </PrivateRoute>
              </Switch>
            </React.Suspense>
          </Box>
          <TermsAndPrivacyFooter />
        </Box>
      </ContextualNavigationLayout>
    </AddressVerificationProvider>
  );
};

const useStyles = makeStyles({ name: 'OnboardingPage' })(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    maxWidth: '596px',
    padding: theme.spacing(3, 0),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5, 0),
    }
  },
  logo: {
    display: 'flex',
    justifyContent: 'flex-start',
    color: theme.palette.darkSlate.main
  },
  root: {
    alignItems: 'center',
    backgroundColor: grey[50],
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    paddingBottom: theme.spacing(17.5),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: 0
    }
  },
  title: {
    color: theme.palette.darkSlate.main,
  }
}));
